import { Navigate, Outlet } from 'react-router-dom';

import { FullscreenLoader } from '@/shared/components/FullscreenLoader';
import { PATHS } from '@/shared/constants/app/routes.constant.ts';
import { useAuth } from '@/features/auth';
import type { UserRole } from '@/shared/types/auth.type';

interface RoleGuardProps {
  /** 접근 허용할 Role 목록 */
  allowedRoles: UserRole[];
  /** 권한 없을 때 리다이렉트 경로 (기본: '/unauthorized') */
  fallbackPath?: string;
}

/**
 * Role 기반 Guard
 * - AuthGuard 하위에 중첩해서 사용
 * - allowedRoles에 포함된 Role만 통과
 *
 * @example
 * {
 *   element: <AuthGuard />,
 *   children: [
 *     {
 *       element: <RoleGuard allowedRoles={['SUPER_ADMIN']} />,
 *       children: [{ path: ROUTES.ADMIN, ...lazyRoute(...) }],
 *     },
 *   ],
 * }
 */
export default function RoleGuard({
  allowedRoles,
  fallbackPath = PATHS.UNAUTHORIZED,
}: RoleGuardProps) {
  const { user, loading } = useAuth();

  if (loading) return <FullscreenLoader />;

  if (!user?.userRole || !allowedRoles.includes(user.userRole)) {
    return <Navigate to={fallbackPath} replace />;
  }

  return <Outlet />;
}
